import React, { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useLocalStorage from '../hooks/useLocalStorage';

const FONT_SIZE_STEP = 0.1;
const MIN_FONT_SIZE_MULTIPLIER = 0.8;
const MAX_FONT_SIZE_MULTIPLIER = 1.5;

const FontSizeContext = createContext({
  fontSizeMultiplier: 1,
  increaseFontSize: () => {},
  decreaseFontSize: () => {},
  resetFontSize: () => {},
});

export const useFontSize = () => useContext(FontSizeContext);

export const FontSizeProvider = ({ children }) => {
  const [fontSizeMultiplier, setFontSizeMultiplier] = useLocalStorage('font_size_multiplier', 1);

  const increaseFontSize = useCallback(() => {
    setFontSizeMultiplier(prev => Math.min(MAX_FONT_SIZE_MULTIPLIER, prev + FONT_SIZE_STEP));
  }, [setFontSizeMultiplier]);

  const decreaseFontSize = useCallback(() => {
    setFontSizeMultiplier(prev => Math.max(MIN_FONT_SIZE_MULTIPLIER, prev - FONT_SIZE_STEP));
  }, [setFontSizeMultiplier]);

  const resetFontSize = useCallback(() => {
    setFontSizeMultiplier(1);
  }, [setFontSizeMultiplier]);

  const contextValue = useMemo(() => ({
    fontSizeMultiplier,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
  }), [fontSizeMultiplier, increaseFontSize, decreaseFontSize, resetFontSize]);

  return (
    <FontSizeContext.Provider value={contextValue}>
      {children}
    </FontSizeContext.Provider>
  );
};

FontSizeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FontSizeContext;